import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import type { FeedbackItem } from "./api";

/**
 * Handlers that apply created, edited, and deleted feedback to the cached
 * feedback list under `queryKey`, so the page updates without a refetch.
 */
export function useFeedbackCache(queryKey: QueryKey) {
  const queryClient = useQueryClient();

  const updateCachedFeedback = (
    update: (current: FeedbackItem[]) => FeedbackItem[],
  ) => {
    queryClient.setQueryData<FeedbackItem[]>(queryKey, (current) =>
      update(current ?? []),
    );
  };

  return {
    handleFeedbackCreated: (newFeedback: FeedbackItem) => {
      updateCachedFeedback((current) => [newFeedback, ...current]);
    },
    handleFeedbackUpdated: (feedbackId: string, body: string) => {
      updateCachedFeedback((current) =>
        current.map((f) => (f.id === feedbackId ? { ...f, body } : f)),
      );
    },
    handleFeedbackDeleted: (feedbackId: string) => {
      updateCachedFeedback((current) =>
        current.filter((f) => f.id !== feedbackId),
      );
    },
  };
}
